import type { PresentationOptionV2 } from 'paintscout'
import { movePresentationNextBlock, movePresentationNextSection } from '@paintscout/util/builder'

/**
 * react-beautiful-dnd droppable types. Two independent drags exist in the editor: blocks move
 * between sections, sections move between pages. Separate types stop a block being dropped into a
 * page's section list and vice versa.
 */
export const PRESENTATION_BLOCK_DND_TYPE = 'presentation-block'
export const PRESENTATION_SECTION_DND_TYPE = 'presentation-section'

/** The subset of react-beautiful-dnd's DropResult this module needs. */
export interface PresentationDragResult {
  type?: string
  draggableId: string
  source: { droppableId: string; index: number }
  destination?: { droppableId: string; index: number } | null
}

/**
 * Applies a drop to the presentation, or returns `null` when there's nothing to do (dropped outside
 * any droppable, or back in its original spot).
 *
 * Purely a translation layer: a droppableId is the container's key (a section key for a block drag,
 * a page key for a section drag) and draggableId is the moved node's key, so the actual mutation is
 * delegated to the tree helpers in @paintscout/util. Those address nodes by key rather than by index
 * path, which is what makes a stale dnd result a safe no-op instead of a wrong move — so this
 * module no longer needs the invariant-violation logging the index-based version carried.
 */
export function applyPresentationDragResult(
  presentation: PresentationOptionV2,
  result: PresentationDragResult
): PresentationOptionV2 | null {
  const { type, draggableId, source, destination } = result

  if (!destination) {
    return null
  }
  if (source.droppableId === destination.droppableId && source.index === destination.index) {
    return null
  }

  if (type === PRESENTATION_SECTION_DND_TYPE) {
    return movePresentationNextSection({
      presentation,
      sectionKey: draggableId,
      toPageKey: destination.droppableId,
      toIndex: destination.index
    })
  }

  return movePresentationNextBlock({
    presentation,
    blockKey: draggableId,
    toSectionKey: destination.droppableId,
    toIndex: destination.index
  })
}
